/**
 * Tiny in-memory cache for picklist-style GET endpoints (integrations,
 * models, voices, prompt templates, etc.). The agent editor fetches these
 * on every inspector mount; before this layer, a remote Y.Doc update would
 * remount the form and re-fetch N picklists over the network, causing
 * visible lag between a peer typing and the user's UI settling.
 *
 * Design:
 *   - `fetchCached(key, fetcher, {ttlMs, staleWhileRevalidateMs})`
 *       1. If a fresh (age < ttlMs) value exists, return it immediately.
 *       2. If a stale-but-not-expired (age < ttlMs + SWR) value exists,
 *          return it AND kick a background revalidation.
 *       3. Otherwise, fetch in the foreground. In-flight promises are
 *          deduplicated so concurrent callers share one fetch.
 *   - `bumpCacheEpoch()` invalidates every cached entry without touching
 *     the promise dedupe map. Called on tenant switch so picklists don't
 *     show stale per-tenant content.
 *   - Module-local Map: per-tab, per-page-reload. Fits the scope of a
 *     typical editor session.
 *
 * Not appropriate for:
 *   - Mutating endpoints (always use plain fetch)
 *   - Endpoints that depend on caller-specific session state not captured
 *     by the cache key
 *   - Large response bodies (the Map holds every entry forever until
 *     bumpCacheEpoch or page reload)
 */

type Entry<T> = {
  value: T;
  fetchedAt: number;
  epoch: number;
};

interface CacheOptions {
  /** How long a value is considered fresh. Returning immediately. */
  ttlMs: number;
  /** Additional window during which a stale value is returned AND a
   *  background revalidation is kicked off. Default = ttlMs. */
  staleWhileRevalidateMs?: number;
}

const cache = new Map<string, Entry<unknown>>();
const inflight = new Map<string, Promise<unknown>>();
let currentEpoch = 0;

/**
 * Fetch with cache. Key must be stable across calls for the same logical
 * query. Include tenant id + any filter params (provider, service, etc.)
 * in the key so different callers don't collide.
 */
export async function fetchCached<T>(
  key: string,
  fetcher: () => Promise<T>,
  options: CacheOptions,
): Promise<T> {
  const ttl = options.ttlMs;
  const swr = options.staleWhileRevalidateMs ?? ttl;
  const now = Date.now();
  const existing = cache.get(key) as Entry<T> | undefined;

  // Fresh hit — return immediately.
  if (
    existing &&
    existing.epoch === currentEpoch &&
    now - existing.fetchedAt < ttl
  ) {
    return existing.value;
  }

  // Stale but within SWR window — return stale, kick background fetch.
  if (
    existing &&
    existing.epoch === currentEpoch &&
    now - existing.fetchedAt < ttl + swr
  ) {
    // Fire-and-forget refresh; errors are swallowed — the next call will
    // try again naturally.
    void revalidate(key, fetcher);
    return existing.value;
  }

  // Miss or expired — foreground fetch with promise dedup.
  const pending = inflight.get(key) as Promise<T> | undefined;
  if (pending) return pending;

  const p = fetcher()
    .then((value) => {
      cache.set(key, { value, fetchedAt: Date.now(), epoch: currentEpoch });
      return value;
    })
    .finally(() => {
      inflight.delete(key);
    });
  inflight.set(key, p);
  return p;
}

async function revalidate<T>(
  key: string,
  fetcher: () => Promise<T>,
): Promise<void> {
  if (inflight.has(key)) return;
  const p = fetcher()
    .then((value) => {
      cache.set(key, { value, fetchedAt: Date.now(), epoch: currentEpoch });
    })
    .catch(() => {
      /* swallow — keep the stale entry rather than blowing up. */
    })
    .finally(() => {
      inflight.delete(key);
    });
  inflight.set(key, p as Promise<unknown>);
}

/**
 * Invalidate every cached entry. Call when the identity of "what the user
 * is looking at" changes in a way that could shift picklist contents
 * (tenant switch, logout, workspace swap).
 */
export function bumpCacheEpoch(): void {
  currentEpoch += 1;
}

/**
 * Drop a specific key — useful when a mutation creates/deletes a resource
 * that another picklist surfaces (e.g. creating an integration should
 * invalidate the integrations-by-provider cache).
 */
export function invalidateCacheKey(key: string): void {
  cache.delete(key);
}

/** Visible for tests / debug — not exported from an index. */
export function __peekCache(): ReadonlyMap<string, Entry<unknown>> {
  return cache;
}
